import { OpenInNew } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslate } from 'react-admin';

const APP_URL = process.env.REACT_APP_APP_URL ?? '';

const buildEventUrl = (slug?: string | null) => {
  if (!slug) return '';
  const base = APP_URL.replace(/\/+$/, '');
  return `${base}/prediction/sports/event/${slug}`;
};

const formatStartTime = (value?: string | null) => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : '';
};

type EventsListProps = {
  events: any[];
  loading?: boolean;
  searchValue: string;
  selectedDay: string;
  selectedIds: Set<string>;
  onSelectItem: (item: any) => void;
};

export default function EventsList({
  events,
  loading,
  searchValue,
  selectedDay,
  selectedIds,
  onSelectItem,
}: EventsListProps) {
  const translate = useTranslate();

  const results = useMemo(() => {
    const keyword = searchValue.trim().toLowerCase();
    return events.filter((event) => {
      if (selectedDay) {
        const eventDay = event.startTime ? dayjs(event.startTime).format('YYYY-MM-DD') : '';
        if (eventDay !== selectedDay) return false;
      }
      if (!keyword) return true;
      const haystack = [
        event.title,
        event.slug,
        `${event.id}`,
        ...(event.markets ?? []).flatMap((m: any) => [m.question, m.slug, `${m.id}`]),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(keyword);
    });
  }, [events, searchValue, selectedDay]);

  const handleSelectEvent = (event: any) => {
    onSelectItem({ ...event, type: 'EVENT', question: event.title });
  };

  if (loading && events.length === 0) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={20} />
      </Stack>
    );
  }

  if (results.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {translate('module.worldCup.breakingEvents.noEvents')}
      </Typography>
    );
  }

  return (
    <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
      <List sx={{ py: 0 }}>
        {results.map((eventItem, index) => {
          const eventId = `${eventItem.id}`;
          const isEventAdded = selectedIds.has(eventId);
          const eventLocalDay = eventItem.startTime
            ? dayjs(eventItem.startTime).format('YYYY-MM-DD')
            : '';
          const eventDayMismatch = Boolean(
            selectedDay && eventLocalDay && eventLocalDay !== selectedDay
          );

          return (
            <Box key={`${eventId}-${index}`}>
              <Stack direction="row" alignItems="center">
                <ListItemButton
                  disabled={isEventAdded || eventDayMismatch}
                  onClick={() => handleSelectEvent(eventItem)}
                  sx={{ px: 1, flex: 1 }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                    <Avatar
                      src={eventItem.icon || eventItem.image}
                      alt={eventItem.title}
                      variant="rounded"
                      sx={{ width: 40, height: 40 }}
                    />
                    <ListItemText
                      primary={eventItem.title}
                      secondary={
                        <Box component="span" sx={{ display: 'block' }}>
                          {formatStartTime(eventItem.startTime) ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {translate('module.worldCup.breakingEvents.startTime', {
                                time: formatStartTime(eventItem.startTime),
                              })}
                            </Typography>
                          ) : null}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {`ID: ${eventItem.id}`}
                          </Typography>
                          {eventItem.slug && APP_URL ? (
                            <Link
                              href={buildEventUrl(eventItem.slug)}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="caption"
                              underline="hover"
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.3,
                                mt: 0.3,
                              }}
                            >
                              {eventItem.slug}
                              <OpenInNew sx={{ fontSize: 12 }} />
                            </Link>
                          ) : null}
                        </Box>
                      }
                    />
                    {isEventAdded ? (
                      <Chip
                        label={translate('module.worldCup.breakingEvents.pinned')}
                        size="small"
                        color="primary"
                        sx={{ fontWeight: 600, flexShrink: 0 }}
                      />
                    ) : null}
                  </Stack>
                </ListItemButton>
              </Stack>

              {index < results.length - 1 ? <Divider /> : null}
            </Box>
          );
        })}
      </List>
    </Box>
  );
}
